import { apiErrorMessage } from '../../data/apiClient';
import { switchOrg } from '../../data/Context';
import {
    AdminOrganization,
    deleteOrganization,
    listAdminOrganizations,
} from '../../data/Organization';
import { useAuth } from '../../hooks/useAuth';
import { useCsrf } from '../../hooks/useCsrf';
import { usePermissions } from '../../hooks/usePermissions';
import {
    ActionIcon,
    Avatar,
    Button,
    Flex,
    Group,
    Loader,
    Modal,
    Paper,
    SimpleGrid,
    Skeleton,
    Stack,
    Text,
    Title,
    Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowsExchange, IconTrash } from '@tabler/icons-react';
import { AxiosError } from 'axios';
import { useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';

/**
 * Display text per count key, as [singular, plural].
 *
 * Kept separate from the keys themselves: the response passes through
 * ConvertEntityPropertiesToCamelCase, so any key doubling as a label comes out
 * mangled — `device tags` arrived as `deviceTags` and rendered as "3 deviceTags".
 */
const COUNT_LABELS: Record<keyof AdminOrganization['counts'], [string, string]> = {
    users: ['user', 'users'],
    locations: ['location', 'locations'],
    devices: ['device', 'devices'],
    deviceTags: ['device tag', 'device tags'],
    scenes: ['scene', 'scenes'],
    components: ['component', 'components'],
    playlists: ['playlist', 'playlists'],
    images: ['image', 'images'],
};

/** Counts that block deletion, rendered as "3 users · 12 devices". */
const describeCounts = (counts: AdminOrganization['counts']): string => {
    const parts = (
        Object.entries(counts) as [keyof AdminOrganization['counts'], number][]
    )
        .filter(([, total]) => total > 0)
        .map(([key, total]) => {
            const [singular, plural] = COUNT_LABELS[key] ?? [key, key];

            return `${total} ${total === 1 ? singular : plural}`;
        });

    return parts.length > 0 ? parts.join(' · ') : 'Empty';
};

const isEmptyOrg = (org: AdminOrganization): boolean =>
    Object.values(org.counts).every(total => total === 0);

/**
 * Platform-wide organization administration. Watchly super admins only.
 */
const AdminOrganizationsPage = () => {
    const { showAdminTools, isSuperAdmin, isLoading: authLoading } = usePermissions();
    const { organization, activeOrg } = useAuth();
    const { csrfToken } = useCsrf();
    const { mutate: globalMutate } = useSWRConfig();

    const {
        data: orgs,
        error,
        isLoading,
        mutate,
    } = useSWR<AdminOrganization[], AxiosError>(
        showAdminTools ? 'api/admin/organizations' : null,
        listAdminOrganizations,
    );

    const [target, setTarget] = useState<AdminOrganization | null>(null);
    const [opened, { open, close }] = useDisclosure(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [switching, setSwitching] = useState<string | null>(null);

    const confirmDelete = (org: AdminOrganization) => {
        setTarget(org);
        open();
    };

    const handleDelete = async () => {
        if (!target) return;
        setIsDeleting(true);
        try {
            await deleteOrganization(csrfToken, target.id);
            await mutate();
            await globalMutate('api/organizations');
            notifications.show({ message: `Deleted ${target.name}.` });
            close();
            setTarget(null);
        } catch (err: any) {
            notifications.show({
                color: 'red',
                title: 'Could not delete organization',
                message: apiErrorMessage(err),
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSwitch = async (org: AdminOrganization) => {
        setSwitching(org.id);
        try {
            await switchOrg(csrfToken, org.id);
            // The active org changed, so every cached response is now stale.
            await globalMutate(() => true, undefined, { revalidate: true });
            notifications.show({ message: `Now viewing ${org.name}.` });
        } catch (err: any) {
            notifications.show({
                color: 'red',
                title: 'Could not switch',
                message: apiErrorMessage(err),
            });
        } finally {
            setSwitching(null);
        }
    };

    if (authLoading) {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        );
    }

    // UX gate only — /api/admin/organizations enforces the role server-side.
    // Reached by URL only, since the nav entry is hidden in the same conditions.
    if (!showAdminTools) {
        return (
            <Stack px="md">
                <Title order={3}>Organizations</Title>
                <Text c="dimmed">
                    {isSuperAdmin
                        ? 'Exit the customer view to manage organizations.'
                        : 'You don’t have permission to view organizations.'}
                </Text>
            </Stack>
        );
    }

    if (error) {
        return (
            <Stack px="md">
                <Title order={3}>Organizations</Title>
                <Text c="red">Error: {error.message}</Text>
            </Stack>
        );
    }

    const currentlyViewing = activeOrg?.id ?? organization?.id;

    return (
        <Stack px="md" gap="lg">
            <div>
                <Title order={3}>Organizations</Title>
                <Text size="sm" c="dimmed">
                    Every customer on the platform. Only empty organizations can be deleted.
                </Text>
            </div>

            {isLoading ? (
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                    {[0, 1, 2].map(i => (
                        <Skeleton key={i} height={148} radius="md" />
                    ))}
                </SimpleGrid>
            ) : orgs && orgs.length > 0 ? (
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                    {orgs.map(org => {
                        const deletable = isEmptyOrg(org);
                        const isOwnOrg = org.id === organization?.id;
                        const isViewing = org.id === currentlyViewing;

                        return (
                            <Paper
                                key={org.id}
                                shadow="xs"
                                p="lg"
                                radius="md"
                                withBorder
                                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                            >
                                <Flex justify="space-between" align="flex-start" gap="xs">
                                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                                        <Avatar src={org.avatarUrl} radius="md" size={40}>
                                            {org.name?.charAt(0)?.toUpperCase()}
                                        </Avatar>
                                        <Text fw={600} truncate>
                                            {org.name}
                                        </Text>
                                    </Group>
                                    <Tooltip
                                        label={
                                            isOwnOrg
                                                ? 'You can’t delete your own organization'
                                                : deletable
                                                  ? 'Delete organization'
                                                  : 'Only empty organizations can be deleted'
                                        }
                                        withArrow
                                    >
                                        <div>
                                            <ActionIcon
                                                variant="subtle"
                                                color="red"
                                                disabled={!deletable || isOwnOrg}
                                                onClick={() => confirmDelete(org)}
                                                aria-label={`Delete ${org.name}`}
                                            >
                                                <IconTrash size={18} />
                                            </ActionIcon>
                                        </div>
                                    </Tooltip>
                                </Flex>

                                <Text size="xs" c="dimmed">
                                    {describeCounts(org.counts)}
                                </Text>

                                <Button
                                    mt="auto"
                                    fullWidth
                                    variant={isViewing ? 'filled' : 'light'}
                                    color={isViewing ? 'orange' : undefined}
                                    leftSection={<IconArrowsExchange size={16} />}
                                    loading={switching === org.id}
                                    disabled={isViewing}
                                    onClick={() => handleSwitch(org)}
                                >
                                    {isViewing ? 'Currently viewing' : 'Switch into'}
                                </Button>
                            </Paper>
                        );
                    })}
                </SimpleGrid>
            ) : (
                <Text c="dimmed">No organizations found.</Text>
            )}

            <Modal opened={opened} onClose={close} title="Danger Zone" centered>
                <Stack gap="sm">
                    <Text style={{ marginBottom: 16 }}>
                        Permanently delete{' '}
                        <Text span fw={700}>
                            {target?.name}
                        </Text>
                        ? This cannot be undone.
                    </Text>
                    <Button color="red" onClick={handleDelete} fullWidth disabled={isDeleting}>
                        {isDeleting ? <Loader color="gray" size={18} /> : 'Confirm Delete'}
                    </Button>
                    <Button color="gray" onClick={close} fullWidth disabled={isDeleting}>
                        Cancel
                    </Button>
                </Stack>
            </Modal>
        </Stack>
    );
};

export { AdminOrganizationsPage };
